<template>
    <div class="rounded-2xl border-2 bg-white overflow-hidden" :class="borderClass">
        <!-- Header -->
        <div class="px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4" :class="headerBgClass">
            <div class="flex flex-col gap-0.5 min-w-0">
                <div class="flex items-center gap-1.5">
                    <BaseHeader size="m" usage="nadia">{{ title }}</BaseHeader>
                    <slot name="title-suffix"></slot>
                </div>
                <p v-if="subtitle" class="body-small text-slate-500">{{ subtitle }}</p>
            </div>

            <!-- Completed: checkmark -->
            <i v-if="completed" class="bi bi-check-circle-fill text-3xl text-valence-primary-500 shrink-0" aria-hidden="true"></i>

            <!-- Next to unlock: primary CTA -->
            <BaseButton v-else-if="unlocked" theme="primary" size="md" class="shrink-0" @click="emit('cta-click')">
                {{ tierConfig.ctaLabel }}
            </BaseButton>

            <!-- Locked: disabled primary button -->
            <BaseButton v-else theme="primary" size="md" class="shrink-0" disabled>
                {{ lockedLabel }}
            </BaseButton>
        </div>

        <!-- Preview insights -->
        <div class="flex flex-col gap-3 px-6 pt-3" :class="previewOpen ? 'pb-5' : 'pb-0'">
            <button type="button" class="flex items-center w-full" @click="previewOpen = !previewOpen">
                <p class="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    {{ completed ? "Preview of insights to expect" : "Preview of insights you'll unlock" }}
                </p>
                <i class="bi bi-chevron-down text-base text-slate-600 ml-1.5 transition-transform duration-200" :class="{ 'rotate-180': previewOpen }" aria-hidden="true"></i>
            </button>
            <div class="grid transition-[grid-template-rows] duration-300 ease-in-out" :style="{ gridTemplateRows: previewOpen ? '1fr' : '0fr' }">
                <div class="overflow-hidden">
                    <div class="relative flex flex-col gap-3">
                        <MyWorkInsightPreviewCard
                            v-for="insight in previewInsights"
                            :key="insight.title"
                            :category="insight.category"
                            :title="insight.title"
                            :summary="insight.summary"
                            :tier="tier"
                            :unlocked="completed" />
                        <div v-if="!completed" class="absolute inset-0 flex items-center justify-center">
                            <i class="bi bi-lock-fill text-6xl text-slate-400 drop-shadow translate-y-2" aria-hidden="true"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseHeader from "~vue/components/BaseHeader.vue";
import { TIER_COLORS } from "~vue/Labs/insightCategory";
import MyWorkInsightPreviewCard from "~vue/Labs/MyWorkInsightPreviewCard.vue";
import { computed, ref } from "vue";

const emit = defineEmits<{ "cta-click": [] }>();

interface PreviewInsight {
    category: string;
    title: string;
    summary: string;
}

const props = withDefaults(
    defineProps<{
        tier: 1 | 2 | 3 | 4;
        title: string;
        subtitle?: string;
        completed: boolean;
        unlocked: boolean;
        previewInsights: PreviewInsight[];
        lockedLabel?: string;
    }>(),
    { lockedLabel: "Connect calendar first" },
);

const TIER_CONFIG: Record<1 | 2 | 3 | 4, { ctaLabel: string }> = {
    1: { ctaLabel: "Connect calendar" },
    2: { ctaLabel: "Complete Perspective" },
    3: { ctaLabel: "Complete Profile" },
    4: { ctaLabel: "Connect Work IQ" },
};

const previewOpen = ref(!props.completed);

const tierConfig = computed(() => TIER_CONFIG[props.tier]);

const colors = computed(() => TIER_COLORS[props.tier]);
const borderClass = computed(() => colors.value.border);
const headerBgClass = computed(() => colors.value.headerBg);
</script>
